import { useEffect, useState } from "react";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Download, FileText, FileSpreadsheet, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { csvToDataset, datasetToCsv } from "../lib/csv_parse";
import { xlsxToDataset } from "../lib/xlsx_parse";
import { fmtMoney } from "../lib/format";

const MODES = [
  { id: "csv", label: "Paste from workbook", hint: "Excel (.xlsx), CSV or TSV" },
  { id: "json", label: "JSON", hint: "Full dataset object" },
];

const MONTH_OPTIONS = (() => {
  // May of each of the past 2 years + next year — the firm's cycle anchor.
  const now = new Date();
  const y = now.getFullYear();
  return [y - 2, y - 1, y, y + 1].map((yr) => `${yr}-05`);
})();

// Re-feed dialog with the incident-hardened flow:
//   parse (xlsx/csv/json) → preview (server-side, no write) → operator
//   confirms diff + month mapping → PUT ?confirmed=true. An automatic
//   pre-import snapshot fires server-side before the replace.
export default function DataUploadDialog({ open, onClose }) {
  const { data, replaceData, refresh } = useData();
  const [mode, setMode] = useState("csv");
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);        // client-side parse from .xlsx/.csv
  const [preview, setPreview] = useState(null);       // server-side preview response
  const [startMonth, setStartMonth] = useState(() => {
    const now = new Date();
    const y = now.getMonth() + 1 >= 5 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-05`;
  });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("prepare");        // prepare | preview | done

  useEffect(() => {
    if (open) {
      setMode("csv"); setText(""); setParsed(null); setPreview(null); setStep("prepare");
    }
  }, [open]);

  if (!open) return null;
  const close = () => { onClose(); setText(""); setParsed(null); setPreview(null); setStep("prepare"); };

  const buildPayload = () => {
    if (parsed) return parsed.dataset;
    if (mode === "json") return JSON.parse(text);
    return csvToDataset(text, { existing: data });
  };

  const requestPreview = async () => {
    setBusy(true);
    try {
      const payload = buildPayload();
      const res = await api.post(`/data/preview?startMonth=${encodeURIComponent(startMonth)}`, payload);
      setPreview(res.data);
      setStep("preview");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Could not preview";
      toast.error(typeof msg === "string" ? msg : "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const payload = buildPayload();
      await api.put(
        `/data?startMonth=${encodeURIComponent(startMonth)}&confirmed=true`,
        payload,
      );
      await refresh?.();
      toast.success(
        `Dataset replaced · ${preview.projectCount} projects · pre-import snapshot saved`,
        { description: "Old dataset is one click away in Backups if anything looks off." }
      );
      close();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Import failed";
      toast.error(typeof msg === "string" ? msg : "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    const isXlsx = /\.(xlsx|xlsm|xls)$/i.test(f.name);
    if (mode === "csv" && isXlsx) {
      setBusy(true);
      try {
        const buf = await f.arrayBuffer();
        const result = xlsxToDataset(buf, { existing: data });
        setParsed(result); setText("");
        toast.success(`Read ${result.dataset.projects.length} projects from ${f.name}`);
      } catch (err) {
        toast.error(err.message || "Could not parse spreadsheet");
      } finally { setBusy(false); }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || "")); setParsed(null); };
    reader.readAsText(f);
  };

  const loadCurrent = () => {
    if (!data) return;
    if (mode === "json") {
      const out = { meta: data.meta, people: data.people, principals: data.principals, projects: data.projects };
      setText(JSON.stringify(out, null, 2));
    } else { setText(datasetToCsv(data)); }
  };

  const downloadTemplate = () => {
    if (!data) return;
    const isJson = mode === "json";
    const content = isJson
      ? JSON.stringify({ meta: data.meta, people: data.people, principals: data.principals, projects: data.projects }, null, 2)
      : datasetToCsv(data);
    const blob = new Blob([content], { type: isJson ? "application/json" : "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = isJson ? "monk-mackenzie-template.json" : "monk-mackenzie-template.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const placeholder = mode === "csv"
    ? "Paste rows from your spreadsheet (TSV) or CSV file. Format:\nType\tProject ID\tProject\tPrincipal\tContract\tNotes\tLabel\tProbability\tMay\tJune\t…"
    : '{ "meta": {...}, "people": [...], "principals": [...], "projects": [...] }';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade"
      onClick={close}
      data-testid="upload-dialog-backdrop"
    >
      <div
        className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col border border-[var(--rule-strong)] animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="upload-dialog"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--rule)]">
          <div>
            <div className="eyebrow">Data · re-feed</div>
            <h3 className="font-display text-lg mt-1" data-testid="upload-dialog-title">
              {step === "prepare" ? "Prepare dataset" : step === "preview" ? "Confirm import" : "Done"}
            </h3>
          </div>
          <button onClick={close} className="p-2 hover:bg-[var(--paper-soft)]" data-testid="upload-close">
            <X size={16} />
          </button>
        </div>

        {step === "prepare" && (
          <>
            <div className="flex border-b border-[var(--rule)]">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { setMode(m.id); setText(""); setParsed(null); }}
                  className={`flex-1 px-6 py-3 text-left border-r last:border-r-0 border-[var(--rule)] ${mode === m.id ? "bg-[var(--paper-soft)]" : "bg-white"}`}
                  data-testid={`upload-mode-${m.id}`}
                >
                  <div className={`font-display tracked-tight uppercase text-xs ${mode === m.id ? "text-[var(--ink)]" : "text-[var(--soft-fg)]"}`}>{m.label}</div>
                  <div className="eyebrow normal-case tracking-[0.06em] text-[var(--soft-fg)] mt-1">{m.hint}</div>
                </button>
              ))}
            </div>

            <div className="p-6 flex-1 overflow-auto thin-scroll">
              <div className="mb-5 border border-[var(--rule)] bg-[var(--paper-soft)] p-4">
                <div className="eyebrow mb-2">Cycle start month</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <select
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    className="border border-[var(--rule-strong)] bg-white px-3 py-2 text-sm font-mono-num outline-none focus:border-[var(--ink)]"
                    data-testid="upload-start-month"
                  >
                    {MONTH_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <span className="text-[11px] font-mono-num text-[var(--soft-fg)]">
                    The Excel's bare month names (May, June, …) will map to YYYY-MM starting here.
                    Preview will show the full mapping so you can catch a wrong year before it writes.
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <label className="px-3 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase cursor-pointer hover:bg-[var(--paper-soft)]">
                  <span className="inline-flex items-center gap-2">
                    {mode === "csv" ? <FileSpreadsheet size={12} /> : <FileText size={12} />} Choose file
                  </span>
                  <input type="file" onChange={onFile} className="hidden"
                    accept={mode === "csv" ? ".xlsx,.xlsm,.xls,.csv,.tsv,.txt" : "application/json,.json"}
                    data-testid="upload-file-input" />
                </label>
                <button type="button" onClick={loadCurrent}
                  className="px-3 py-2 border border-[var(--rule)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
                  data-testid="upload-load-current">Load current</button>
                <button type="button" onClick={downloadTemplate}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] ml-auto"
                  data-testid="upload-download-template">
                  <Download size={12} /> Template
                </button>
              </div>

              {parsed ? (
                <div className="mt-5 border border-[var(--rule-strong)] bg-[var(--paper-soft)] p-4" data-testid="upload-parsed-summary">
                  <div className="eyebrow text-[var(--ink)] mb-2 inline-flex items-center gap-2">
                    <FileSpreadsheet size={12} /> Parsed spreadsheet
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
                    <Stat label="Projects" value={parsed.dataset.projects.length} testid="parsed-projects" />
                    <Stat label="People" value={parsed.dataset.people.length} testid="parsed-people" />
                    <Stat label="Principals" value={parsed.dataset.principals.length} testid="parsed-principals" />
                    <Stat label="Months" value={parsed.dataset.meta.months.length} testid="parsed-months" />
                  </div>
                </div>
              ) : (
                <textarea
                  value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={placeholder}
                  className="mt-5 w-full h-56 font-mono-num text-[11px] leading-snug p-3 border border-[var(--rule)] outline-none focus:border-[var(--ink)] bg-[var(--paper-soft)] whitespace-pre"
                  data-testid="upload-textarea" spellCheck={false} />
              )}
            </div>

            <div className="p-6 border-t border-[var(--rule)] flex justify-end gap-2">
              <button onClick={close}
                className="px-4 py-2 text-xs font-display tracked-tight uppercase border border-[var(--rule)] hover:bg-[var(--paper-soft)]"
                data-testid="upload-cancel">Cancel</button>
              <button onClick={requestPreview}
                disabled={busy || (!parsed && !text.trim())}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-display tracked-tight uppercase bg-[var(--ink)] text-white disabled:opacity-40 hover:opacity-90"
                data-testid="upload-preview">
                {busy ? "Previewing…" : (<>Preview import <ArrowRight size={12} /></>)}
              </button>
            </div>
          </>
        )}

        {step === "preview" && preview && (
          <>
            <div className="p-6 flex-1 overflow-auto thin-scroll space-y-5">
              {/* Warnings — collisions are the hard variant, styled red */}
              {preview.warnings?.length > 0 && (
                <div className="border border-[#6B2F2A]/30 bg-[#FBF3F2] p-3" data-testid="preview-warnings">
                  <div className="eyebrow text-[#6B2F2A] mb-1.5 inline-flex items-center gap-2">
                    <AlertTriangle size={11} /> Warnings
                  </div>
                  <ul className="text-[11px] font-mono-num text-[#6B2F2A] leading-relaxed list-disc pl-5">
                    {preview.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                </div>
              )}
              {preview.collisions?.length > 0 && (
                <div className="border-2 border-[#6B2F2A] bg-[#FBF3F2] p-3" data-testid="preview-collisions">
                  <div className="eyebrow text-[#6B2F2A] mb-2 inline-flex items-center gap-2">
                    <AlertTriangle size={11} /> Month-key collisions · {preview.collisions.length}
                  </div>
                  <div className="text-[10px] font-mono-num text-[#6B2F2A] mb-2 leading-relaxed">
                    Two source keys map to the same target — one value will overwrite the other on save.
                    Fix the start-month picker or split multi-year data before continuing.
                  </div>
                  <ul className="text-[11px] font-mono-num text-[#6B2F2A] leading-relaxed max-h-40 overflow-auto space-y-1.5">
                    {preview.collisions.slice(0, 20).map((c) => (
                      <li key={`${c.target}-${c.context}`} className="border-l-2 border-[#6B2F2A] pl-2">
                        <div>{c.context}</div>
                        <div className="text-[10px] opacity-80">→ target {c.target} · sources {c.sources?.map((s) => `${s.key}=${s.value}`).join(" + ")}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Totals summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Projects (payload)" value={preview.projectCount} testid="preview-project-count" />
                <Stat label="Projects (current DB)" value={preview.currentProjectCount} testid="preview-current-count" />
                <Stat label="Firm raw" value={fmtMoney(preview.firmRawTotal, { compact: true })} testid="preview-raw" />
                <Stat label="Firm weighted" value={fmtMoney(preview.firmWeightedTotal, { compact: true })} testid="preview-weighted" />
              </div>

              {/* Month-key mapping — the whole point of Step 2 */}
              <div className="border border-[var(--rule)] p-4">
                <div className="eyebrow mb-2">Month-key mapping · anchored at {preview.startMonth}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] font-mono-num" data-testid="preview-mapping">
                  {Object.entries(preview.monthKeyMapping || {}).map(([bare, ym]) => (
                    <div key={bare} className={`flex items-center justify-between ${bare === ym ? "text-[var(--soft-fg)]" : "text-[var(--ink)]"}`}>
                      <span>{bare}</span>
                      <ArrowRight size={9} className="opacity-40" />
                      <span>{ym}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] font-mono-num text-[var(--soft-fg)] mt-3">
                  Sample keys the payload will use: {preview.monthKeySamples?.join(" · ") || "—"}
                </div>
              </div>

              {/* Diff — projects added and (crucially) DELETED */}
              <div className="grid md:grid-cols-2 gap-3">
                <div className="border border-[var(--rule)] p-4">
                  <div className="eyebrow mb-2 inline-flex items-center gap-2 text-[#2C4A38]">
                    <CheckCircle2 size={11} /> New projects · {preview.projectsThatWouldBeAdded?.length || 0}
                  </div>
                  <ul className="text-[11px] font-mono-num text-[var(--ink)] leading-relaxed max-h-32 overflow-auto" data-testid="preview-added">
                    {(preview.projectsThatWouldBeAdded || []).map((p) => (
                      <li key={p.id}>+ {p.id} · {p.name}</li>
                    ))}
                    {(!preview.projectsThatWouldBeAdded || preview.projectsThatWouldBeAdded.length === 0) && (
                      <li className="text-[var(--soft-fg)]">none</li>
                    )}
                  </ul>
                </div>
                <div className={`border p-4 ${preview.projectsThatWouldBeDeleted?.length > 0 ? "border-[#6B2F2A]/30 bg-[#FBF3F2]" : "border-[var(--rule)]"}`}>
                  <div className={`eyebrow mb-2 inline-flex items-center gap-2 ${preview.projectsThatWouldBeDeleted?.length > 0 ? "text-[#6B2F2A]" : ""}`}>
                    <AlertTriangle size={11} /> Projects that would be DELETED · {preview.projectsThatWouldBeDeleted?.length || 0}
                  </div>
                  <ul className="text-[11px] font-mono-num leading-relaxed max-h-32 overflow-auto" data-testid="preview-deleted">
                    {(preview.projectsThatWouldBeDeleted || []).map((p) => (
                      <li key={p.id}>− {p.id} · {p.name}</li>
                    ))}
                    {(!preview.projectsThatWouldBeDeleted || preview.projectsThatWouldBeDeleted.length === 0) && (
                      <li className="text-[var(--soft-fg)]">none</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[var(--rule)] flex justify-between gap-2 flex-wrap">
              <button onClick={() => setStep("prepare")}
                className="px-4 py-2 text-xs font-display tracked-tight uppercase border border-[var(--rule)] hover:bg-[var(--paper-soft)]"
                data-testid="preview-back">
                ← Back
              </button>
              <div className="text-[11px] font-mono-num text-[var(--soft-fg)] mr-auto ml-3 max-w-xs leading-relaxed">
                An automatic snapshot of the current dataset will be saved to Backups before writing.
              </div>
              <button onClick={confirmImport}
                disabled={busy}
                className="px-4 py-2 text-xs font-display tracked-tight uppercase bg-[var(--ink)] text-white disabled:opacity-40 hover:opacity-90"
                data-testid="upload-confirm">
                {busy ? "Importing…" : `Confirm — replace ${preview.currentProjectCount} projects with ${preview.projectCount}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="font-mono-num text-xl mt-1">{value}</div>
    </div>
  );
}
